import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { VButtonStatus } from '@app/directives/_forms/v-input/v-input.component';
import { IVehicle } from '@app/interfaces/IVehicle';
import { ValidationResult } from '@app/main/modules/foundation/foundation.enums';
import { IDeviceToAdd, IValidationResults } from '@app/main/modules/foundation/foundation.interfaces';
import { DeviceDataValidationService, ValidationCheck, ValidationCheckType, ValidationRequestParameters, ValidationStatus } from '@app/main/modules/foundation/_services/device-data-validation.service';
import { UnitsService } from '@app/main/modules/foundation/_services/unit.service';
import { VehicleValidationCheckRecordService, IVehicleValidationCheckRecord } from '@app/main/modules/foundation/_services/vehicle-validation-check-record.service';
import { CurrentUserService } from '@app/services/currentUser.service';
import { DateService, DateUnit } from '@app/services/date.service';
import { TranslationService } from '@app/services/translation.service';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, take, takeUntil } from 'rxjs/operators';
import './device-install-validator.component.scss';

@Component({
    selector: 'device-install-validator',
    template: require('./device-install-validator.component.html')
})
export class DeviceInstallValidatorComponent implements OnChanges, OnDestroy {
    @Input() device: IDeviceToAdd;
    @Input() serial: string;
    @Input() newDevice: boolean;
    @Input() autoRunTests: boolean;
    @Input() peripheralNoTrackerError: boolean;
    @Input() includeTemperatureTests: boolean;
    @Input() commissionVehicle: IVehicle;

    @Output() validationComplete: EventEmitter<IValidationResults> = new EventEmitter();

    //--

    ValidationCheckType = ValidationCheckType;

    categories: ValidationCheckType[];
    results: IValidationResults;

    loading: boolean;

    private destroy$ = new Subject<void>();
    constructor(
        private readonly unitService: UnitsService,
        private readonly dateService: DateService,
        private readonly deviceDataValidationService: DeviceDataValidationService,
        private readonly translationService: TranslationService,
        private readonly currentUserService: CurrentUserService,
        private readonly vehicleValidationCheckRecordService: VehicleValidationCheckRecordService) {

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.serial) {
            return;
        }

        this.searchForSerial();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    get deviceNotFound(): boolean {
        return !this.device.searchResponse && this.serial?.length > 5 && !this.peripheralNoTrackerError;
    }

    get formDetails(): { title: string, subtitle: string } {
        if (this.newDevice) {
            return {
                title: this.device.label,
                subtitle: this.translationService.t('Commissions.DeviceToAddValidationSubtitle')
            }
        }

        return {
            title: " this.translationService.t('Commissions.PreInstalledValidationTitle')",
            subtitle: this.translationService.t('Commissions.PreInstalledValidationSubtitle')
        }
    }

    //--

    private runTests() {
        this.results = {
            result: ValidationResult.Pass,
            checksRun: []
        }

        this.loading = true;

        this.runCoreTests().pipe(take(1)).subscribe(check => {

            if (this.newDevice) {
                this.results.checksRun.push(check);

                if (check.ValidationResults.some(result => result.Status === ValidationStatus.Fail)) {
                    this.saveCheckRecords();
                    this.loading = false;
                    this.results.result = ValidationResult.Fail;
                    return;
                }
            }

            this.runRemainingTests();
        });
    }

    private saveCheckRecords(): void {
        this.currentUserService.getCurrentUser().then(currentUser => {
            const observables: Observable<IVehicleValidationCheckRecord>[] = [];

            this.results.checksRun.forEach(cr => {
                observables.push(
                    this.vehicleValidationCheckRecordService.addVehicleValidationCheckRecords({
                        ValidationCheckType: cr.ValidationCheckType,
                        EventTime: new Date(),
                        UserId: currentUser.Id,
                        VehicleId: this.commissionVehicle?.Id,
                        Serial: this.serial,
                        ValidationCheckResultJson: JSON.stringify(cr.ValidationResults)
                    })
                );
            });

            forkJoin(observables).pipe(takeUntil(this.destroy$)).subscribe(data => {
                this.results.checksRun.forEach(cr => {
                    cr.ValidationResultDatabaseId = data.find(d => d.ValidationCheckType == cr.ValidationCheckType)?.Id
                });

                this.validationComplete.emit(this.results);
            });
        });
    }

    private runCoreTests(): Observable<ValidationCheck> {
        if (!this.categories?.find(cat => cat === ValidationCheckType.Core)) {
            return;
        }

        const validationRequestParameters: ValidationRequestParameters = {
            Serial: this.device.serial,
            CheckType: ValidationCheckType.Core,
            ParentTrackerImei: this.device?.searchResponse?.parentTrackerImei,
            StartTime: this.dateService.addOrSubtractTime(new Date(), -60, DateUnit.Min).toISOString(),
            EndTime: this.dateService.addOrSubtractTime(new Date(), +1, DateUnit.Min).toISOString()
        }

        return this.deviceDataValidationService.getValidationResults(validationRequestParameters);
    }

    private runRemainingTests(): void {
        const categoriesRemaining = this.categories.filter(category => category !== ValidationCheckType.Core);
        if (!categoriesRemaining.length) {
            this.loading = false;
            return;
        }

        if (categoriesRemaining.includes(ValidationCheckType.Temperature) && !!this.device?.searchResponse?.Unit && !this.includeTemperatureTests) {
            const index = categoriesRemaining.indexOf(ValidationCheckType.Temperature, 0);
            if (index > -1) {
                categoriesRemaining.splice(index, 1);
            }
        }

        const observables: Observable<ValidationCheck>[] = [];

        categoriesRemaining.forEach(category => {
            let validationOptions: ValidationRequestParameters = {
                Serial: this.device.serial,
                CheckType: category,
                StartTime: this.dateService.addOrSubtractTime(new Date(), -60, DateUnit.Min).toISOString(),
                EndTime: this.dateService.addOrSubtractTime(new Date(), +1, DateUnit.Min).toISOString()
            }

            if (!!this.device?.searchResponse?.parentTrackerImei) {
                validationOptions.ParentTrackerImei = this.device?.searchResponse?.parentTrackerImei;
            }

            observables.push(this.deviceDataValidationService.getValidationResults(validationOptions).pipe(catchError((err, caught) => this.failedCheck(err, category))));
        });

        forkJoin(observables).pipe(takeUntil(this.destroy$), finalize(() => this.loading = false)).subscribe(data => {
            this.results.checksRun.push(...data)
            this.results.checksRun.forEach(check => {
                if (check.ValidationResults.some(result => result.Status === ValidationStatus.Fail)) {
                    this.results.result = ValidationResult.Fail;
                    return;
                }
            });

            this.saveCheckRecords();
        });
    }

    private failedCheck(err: HttpErrorResponse, checkType: ValidationCheckType): Observable<ValidationCheck> {
        const failureResult: ValidationCheck = {
            ValidationResults: [{
                Status: ValidationStatus.Warning,
                TestResponseString: "Validation run",
                InfoMessageString: `Run failed - ${err.error}`,
                
            }],
            ValidationCheckType: checkType
        };

        return of(failureResult);
    }

    private searchForSerial(): void {
        if (!this.device) {
            this.device = { serial: this.serial, inputStatus: VButtonStatus.Default, buttonStatus: VButtonStatus.Default }
        }
        const parentTrackerReference = this.device.searchResponse?.parentTrackerImei;


        this.unitService.getUnitBySerial(this.serial)
            .pipe(take(1))
            .subscribe(searchResponse => {
                this.device.searchResponse = searchResponse;
                this.device.searchResponse.parentTrackerImei = parentTrackerReference;
                this.createUnitLabel();

                if (!this.device?.searchResponse?.Product) {
                    this.results = { result: ValidationResult.Fail, checksRun: [] }
                    this.validationComplete.emit(this.results);
                    return;
                }        

                this.deviceDataValidationService.getValidationCategories(this.serial).pipe(takeUntil(this.destroy$)).subscribe(categories => {
                    this.categories = categories;

                    this.autoRunTests && this.runTests();
                })
            });

    }

    private createUnitLabel(): void {
        if (!this.device.searchResponse) {
            this.device.label = "";
        }

        if (this.device.searchResponse.Product) {
            this.device.label = `${this.device.searchResponse.vendorString ? this.device.searchResponse.vendorString : ''} ${this.device.searchResponse.Product.Description}`;
            return;
        }

        this.device.label = this.translationService.t("Commissions.ProductInformationUnavailable");

    }
}
